import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { ACCEPT_ERROR, TFileUploadFor } from "@/constants/file";
import { button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import { TFileRules, covertMimeTypesToAccepts, getFileErrorMessage, getFileStatusWithRules, getExtensionDetailOfMimeType } from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import { toast } from "@/utils/toast";

export type TDropZoneProps = {
  value?: TExtendFile | TExtendFile[] | null;
  onChange?: (value: TExtendFile | TExtendFile[] | null) => void;
  options?: Omit<DropzoneOptions, "onDrop">;
  enableFileList?: boolean;
  nonControl?: boolean;
  rules?: TFileRules;
  bordered?: boolean;
  disabled?: boolean;
  uploadFor?: TFileUploadFor;
  // Opt-in: when provided, the caller fully owns rejection handling (size + format) and the
  // default ACCEPT_ERROR toast is skipped. Used to sync error wording with the talent import.
  handleFileRejections?: (fileRejections: FileRejection[]) => void;
  // Opt-in: render the selected file chip in the link/blue style (matches talent import).
  highlightFile?: boolean;
};

export const DropZone: FCC<TDropZoneProps> = ({
  options = {},
  value,
  onChange,
  enableFileList = true,
  children,
  rules = {},
  nonControl = false,
  bordered = true,
  disabled,
  uploadFor,
  handleFileRejections,
  highlightFile = false,
}) => {
  const { field } = useFormField(nonControl);
  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? value ?? []].flat());
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (field?.value ?? value) {
      const newValue: TExtendFile[] = [field?.value ?? value].flat();
      if (files.some((file) => !newValue.find(({ id }) => file.id === id))) {
        setFiles([field?.value ?? value].flat());
      }
    }
  }, [value, field]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // When the caller owns rejection handling, delegate everything (size + format) to it.
      if (handleFileRejections) {
        if (fileRejections.length > 0) {
          handleFileRejections(fileRejections);
          return;
        }
      } else if (fileRejections.length > 0 && rules.acceptMimeTypes) {
        const extension = getExtensionDetailOfMimeType(rules.acceptMimeTypes);
        toast.error(ACCEPT_ERROR(extension));
        return;
      }
      let newFiles = files;
      setUploading(true);
      const droppedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const fileOption: TExtendFileOption = {
            id: makeUuid(),
            status: getFileStatusWithRules(file, rules),
          };
          return Object.assign(file, { ...fileOption, filePath: "" });
        }),
      );

      if (options.multiple) {
        newFiles = [newFiles, droppedFiles].flat();
      } else {
        newFiles = [droppedFiles[0]];
      }
      setFiles(newFiles);
      triggerChangeFiles(newFiles);
      setUploading(false);
    },
    [uploadFor, files, handleFileRejections],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...options,
    onDrop,
    accept: covertMimeTypesToAccepts(rules.acceptMimeTypes),
    // Hard-reject oversize files at the dropzone ONLY when the caller opts in via handleFileRejections.
    // Existing callers (which check maxSize manually after attach) keep their current behavior.
    maxSize: handleFileRejections ? rules.maxSize : undefined,
    disabled: uploading || disabled,
  });

  const removeFile = (removedId: Key) => {
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    setFiles(updatedFiles);
    onChange?.(updatedFiles);
    triggerChangeFiles(updatedFiles);
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    const { multiple } = options;
    onChange?.(multiple === false ? (files.at(0) ?? null) : files);
    field?.onChange(multiple === false ? (files.at(0) ?? null) : files);
    field?.onBlur();
  };

  return (
    <Box>
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        <DropZoneArea dragged={isDragActive} bordered={bordered}>
          {children ?? (
            <>
              <Typography variant="body14">アップロードするファイルをドラッグ&ドロップ</Typography>
              <Typography variant="body14">または</Typography>
              <Button variant="outline">ファイルを選択する</Button>
            </>
          )}
          {uploading && <UploadingBackup>作成中…</UploadingBackup>}
        </DropZoneArea>
      </Box>
      {enableFileList && (
        <Stack flexDirection="row" flexWrap="wrap" gap={3} mt={files.length ? 1 : 0}>
          {files.map((file) => (
            <FileItem
              key={file.id}
              file={file}
              highlight={highlightFile}
              onRemove={() => removeFile(file.id)}
              maxWidth={336}
              errorMsg={getFileErrorMessage(file, rules)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const options = { shouldForwardProp: (propName: string) => !["dragged", "bordered"].includes(propName) };

const DropZoneAreaActive = css`
  border-color: ${button.primary};
`;

const DropZoneArea = styled(Stack, options)<{ dragged: boolean; bordered: boolean }>`
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  border: 1px dashed ${divider.middle};
  border-radius: ${rounded.sm};
  overflow: hidden;
  cursor: pointer;
  &:hover {
    ${DropZoneAreaActive}
  }
  ${({ dragged }) => dragged && DropZoneAreaActive}
  ${({ bordered }) =>
    !bordered &&
    css`
      padding: 0;
      border: none;
    `}
`;

const UploadingBackup = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: ${button.disable};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${text.white};
`;
